import { LotacaoAndEstagioSelectFieldModel } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { resolveValue } from 'components/form/final-form/hooks/useField'
import { Calculation } from 'final-form-calculate'
import {
  SexoEnum,
  TipoAtendimentoEnum,
  TipoEstabelecimentoEnum,
  TipoParticipacaoAtendimentoEnum,
} from 'graphql/types.generated'
import { compact, inRange, isEmpty } from 'lodash'
import { ProcedimentosAutomaticosPreNatal, ProcedimentosAutomaticosPuerperio } from 'types/enums'
import { monthsToYearsFloor } from 'util/date/calculateAge'
import { Meta, MetaArray, MetaRoot } from 'util/metaPath'
import { v4 as uuidv4 } from 'uuid'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { ProcedimentoSigtapFieldModel } from 'view/atendimentos/detail/soap/finalizacao/components/ProcedimentoSigtapField'
import { ProcedimentoAutomatico } from 'view/atendimentos/model-atendimento'
import { calculateProcedimentoAutomaticoIndividual } from 'view/atendimentos/utils/util-procedimentoAutomatico'

import { ProblemaCondicaoModel } from '../../avaliacao/components/problemas-condicoes/model-problemasCondicoes'
import {
  hasProblemaCondicaoDePreNatal,
  hasProblemaNaoEvolucaoPuerperio,
} from '../../avaliacao/components/problemas-condicoes/utils/verifications-preNatal'
import { FinalizacaoAtendimentoFormModel } from '../FinalizacaoAtendimentoForm'

const createTipoAtendimentoCalculations = (
  meta: Meta<FinalizacaoAtendimentoFormModel> & MetaRoot<FinalizacaoAtendimentoFormModel>,
  metaProblemasCondicoes: MetaArray<ProblemaCondicaoModel>,
  cbo2002: string,
  procedimentosAutomaticos: ProcedimentoAutomatico[],
  hasPermissionPreNatal: boolean,
  idadeCidadaoEmAnos: number,
  desejaInformarIdentidadeGenero: boolean,
  sexo: SexoEnum,
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  isObservacaoAndResponsavel: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean
): Calculation => ({
  field: [meta.tipoAtendimento.absolutePath(), metaProblemasCondicoes.absolutePath()],
  updates: {
    [meta.procedimentosAdministrativos.absolutePath()]: (
      _: TipoAtendimentoEnum,
      allValues: SoapState,
      prevValues: SoapState
    ) => {
      if (!isEmpty(prevValues)) {
        const isUpa = tipoEstabelecimento === TipoEstabelecimentoEnum.UPA
        const problemasCondicoes = resolveValue<ProblemaCondicaoModel[]>(allValues, metaProblemasCondicoes) || []

        const procedimentoAutomaticoPuerperio = findProcedimentosAutomaticosByCodigo(procedimentosAutomaticos, [
          ProcedimentosAutomaticosPuerperio.CONSULTA_PUERPERAL,
        ])

        const deveAdicionarProcedimentoAutomaticoPuerperio =
          !isUpa &&
          hasProblemaNaoEvolucaoPuerperio(problemasCondicoes) &&
          canAddProcedimentoAutomaticoGestacao(
            procedimentoAutomaticoPuerperio,
            hasPermissionPreNatal,
            idadeCidadaoEmAnos,
            desejaInformarIdentidadeGenero,
            sexo
          )

        if (deveAdicionarProcedimentoAutomaticoPuerperio) {
          return getProcedimentosWithNewProced(
            allValues.finalizacao.procedimentosAdministrativos,
            procedimentoAutomaticoPuerperio
          )
        }

        const procedimentoAutomaticoPreNatal = findProcedimentosAutomaticosByCodigo(procedimentosAutomaticos, [
          ProcedimentosAutomaticosPreNatal.CONSULTA_PRE_NATAL,
        ])

        const deveAdicionarProcedimentoAutomaticoPreNatal =
          !isUpa &&
          hasProblemaCondicaoDePreNatal(problemasCondicoes, hasProblemaComCiapW78AtivoPersistido) &&
          canAddProcedimentoAutomaticoGestacao(
            procedimentoAutomaticoPreNatal,
            hasPermissionPreNatal,
            idadeCidadaoEmAnos,
            desejaInformarIdentidadeGenero,
            sexo
          )

        if (deveAdicionarProcedimentoAutomaticoPreNatal) {
          return getProcedimentosWithNewProced(
            allValues.finalizacao.procedimentosAdministrativos,
            procedimentoAutomaticoPreNatal
          )
        }

        return calculateProcedimentosAutomaticosByTipoAtendimento(
          allValues,
          procedimentosAutomaticos,
          tipoEstabelecimento,
          cbo2002,
          isObservacaoAndResponsavel
        )
      }

      return allValues.finalizacao.procedimentosAdministrativos
    },
  },
})

const canAddProcedimentoAutomaticoGestacao = (
  procedimentosGestacao: ProcedimentoAutomatico[],
  hasPermissionPreNatal: boolean,
  idadeCidadaoEmAnos: number,
  desejaInformarIdentidadeGenero: boolean,
  sexoCidadao: SexoEnum
): boolean => {
  if (hasPermissionPreNatal) {
    const procedimentosValidos = procedimentosGestacao.filter((item) => {
      const { idadeMinima, idadeMaxima, sexo: sexoProcedimento } = item
      const idadeMinimaEmAnos = monthsToYearsFloor(idadeMinima)
      const idadeMaximaEmAnos = monthsToYearsFloor(idadeMaxima)
      const procedimentoValidoParaSexoDoCidadao = !desejaInformarIdentidadeGenero && sexoProcedimento === sexoCidadao
      return (
        inRange(idadeCidadaoEmAnos, idadeMinimaEmAnos, idadeMaximaEmAnos) &&
        (procedimentoValidoParaSexoDoCidadao || desejaInformarIdentidadeGenero)
      )
    })
    return procedimentosValidos.isNotEmpty()
  }
  return false
}

const calculateProcedimentosAutomaticosByTipoAtendimento = (
  allValues: SoapState,
  procedimentosAutomaticos: ProcedimentoAutomatico[],
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  cbo2002: string,
  isProfResponsavelObservacao: boolean
): ProcedimentoSigtapFieldModel[] => {
  const procedimentoAutomatico = calculateProcedimentoAutomaticoIndividual(
    tipoEstabelecimento,
    allValues.finalizacao?.tipoAtendimento,
    cbo2002,
    allValues.plano?.startObservacao || isProfResponsavelObservacao
  )

  return getProcedimentosWithNewProced(
    allValues.finalizacao.procedimentosAdministrativos,
    findProcedimentosAutomaticosByCodigo(procedimentosAutomaticos, [procedimentoAutomatico])
  )
}

export const findProcedimentosAutomaticosByCodigo = (
  procedimentosAutomaticos: ProcedimentoAutomatico[],
  codigoProcedimentos: string[]
) => procedimentosAutomaticos.filter((item) => codigoProcedimentos.includes(item.codigo))

export const getProcedimentosWithNewProced = (
  proceds: ProcedimentoSigtapFieldModel[],
  newProceds: ProcedimentoAutomatico[]
) => {
  let listProcedAdministrativos = (proceds ?? []).filter((proced) => !proced.automatico)

  compact(newProceds).forEach((item) => {
    const containsProced = listProcedAdministrativos.find((proced) => proced.procedimento.codigo === item.codigo)
    if (!containsProced) {
      listProcedAdministrativos.push({
        _id: uuidv4(),
        procedimento: item,
        automatico: true,
      })
    }
  })

  return listProcedAdministrativos
}

export const tipoParticipacaoCidadaoCalculator = (
  meta: Meta<FinalizacaoAtendimentoFormModel> & MetaRoot<FinalizacaoAtendimentoFormModel>
): Calculation => ({
  field: meta.cidadaoPresente.absolutePath(),
  updates: {
    [meta.tipoParticipacaoCidadao.absolutePath()]: (cidadaoPresente: boolean, allValues: SoapState) =>
      cidadaoPresente
        ? allValues.finalizacao.tipoParticipacaoCidadao ?? TipoParticipacaoAtendimentoEnum.PRESENCIAL
        : null,
  },
})

export const tipoParticipacaoProfissionalCalculator = (
  meta: Meta<FinalizacaoAtendimentoFormModel> & MetaRoot<FinalizacaoAtendimentoFormModel>
): Calculation => ({
  field: [meta.atendimentoCompartilhadoLotacao.absolutePath()],
  updates: {
    [meta.tipoParticipacaoProfissionalConvidado.absolutePath()]: (
      lotacaoAtendCompartilhado: LotacaoAndEstagioSelectFieldModel,
      allValues: SoapState
    ) =>
      lotacaoAtendCompartilhado
        ? allValues.finalizacao.tipoParticipacaoProfissionalConvidado ?? TipoParticipacaoAtendimentoEnum.PRESENCIAL
        : null,
  },
})

export const createFinalizacaoIndividualCalculations = (
  meta: Meta<FinalizacaoAtendimentoFormModel> & MetaRoot<FinalizacaoAtendimentoFormModel>,
  metaProblemasCondicoes: MetaArray<ProblemaCondicaoModel>,
  procedimentosAutomaticos: ProcedimentoAutomatico[],
  cbo2002: string,
  hasPermissionPreNatal: boolean,
  idadeCidadaoEmAnos: number,
  desejaInformarIdentidadeGenero: boolean,
  sexo: SexoEnum,
  tipoEstabelecimento: TipoEstabelecimentoEnum,
  isObservacaoAndResponsavel: boolean,
  hasProblemaComCiapW78AtivoPersistido: boolean
): Calculation[] => [
  createTipoAtendimentoCalculations(
    meta,
    metaProblemasCondicoes,
    cbo2002,
    procedimentosAutomaticos,
    hasPermissionPreNatal,
    idadeCidadaoEmAnos,
    desejaInformarIdentidadeGenero,
    sexo,
    tipoEstabelecimento,
    isObservacaoAndResponsavel,
    hasProblemaComCiapW78AtivoPersistido
  ),
  tipoParticipacaoCidadaoCalculator(meta),
  tipoParticipacaoProfissionalCalculator(meta),
]
